import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Drawer,
    List,
    Typography,
    Divider,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Button,
    Link as MuiLink,
    useMediaQuery,
    useTheme,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Home as HomeIcon,
    Newspaper as NewspaperIcon,
    ShoppingCart as ShoppingCartIcon,
    Settings as SettingsIcon,
    AdminPanelSettings as AdminIcon,
    SupervisorAccount as SuperAdminIcon,
    PhotoLibrary as GalleryIcon,
    Forum as SupportIcon,
    Key as KeyIcon,
    Android as AndroidIcon,
    Apple as AppleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { COLORS } from '../theme/colors';
import { getUploadUrl } from '../utils/apiConfig';

const drawerWidth = 280;

/**
 * Sidebar Component
 * Responsive side navigation drawer.
 * 
 * @param {boolean} mobileOpen - Whether mobile drawer is open
 * @param {function} onDrawerToggle - Toggle function for mobile drawer
 */
const Sidebar = ({ mobileOpen, onDrawerToggle }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { effectiveTheme } = useThemeMode();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    // Role checks
    const isAdminOrHigher = user && (user.role === 'admin' || user.role === 'superadmin');
    const isSuperAdmin = user && user.role === 'superadmin';



    // Handle navigation
    const handleNavigate = (path) => {
        navigate(path);
        if (!isDesktop && onDrawerToggle) onDrawerToggle();
    };

    // Build navigation menu
    const menuItems = [
        {
            text: isSuperAdmin ? t('admin.superDashboard') : (isAdminOrHigher ? t('admin.dashboard') : t('common.dashboard')),
            icon: isSuperAdmin ? <SuperAdminIcon /> : (isAdminOrHigher ? <AdminIcon /> : <DashboardIcon />),
            path: isSuperAdmin ? '/superadmin' : (isAdminOrHigher ? '/admin' : '/dashboard'),
            requiredAuth: true
        },
        {
            text: t('common.installations'),
            icon: <HomeIcon />,
            path: '/installations',
            requiredAuth: true
        },
        {
            text: t('app.news'),
            icon: <NewspaperIcon />,
            path: '/news',
            requiredAuth: true
        },
        {
            text: t('gallery.title'),
            icon: <GalleryIcon />,
            path: '/gallery',
            requiredAuth: false
        },
        {
            text: t('support.title'),
            icon: <SupportIcon />,
            path: '/support',
            requiredAuth: false
        },
        {
            text: t('app.shop'),
            icon: <ShoppingCartIcon />,
            path: 'https://www.gobelpower.com',
            requiredAuth: true,
            isExternal: true
        },
        {
            text: t('apiKeys.menuTitle'),
            icon: <KeyIcon />,
            path: '/settings/api-keys',
            requiredAuth: true
        },
        {
            text: t('common.settings'),
            icon: <SettingsIcon />,
            path: '/settings',
            requiredAuth: true
        }
    ];

    // Drawer content
    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ p: 2, mt: { xs: 0, lg: 6 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {user ? (
                    <>
                        <Avatar
                            src={getUploadUrl(user.avatar)}
                            sx={{ mb: 1, width: 64, height: 64, bgcolor: COLORS.brand.orange.main }}
                        >
                            {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="h6">{user.username}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t(`common.${user.role || 'user'}`)}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="h6">{t('app.name')}</Typography>
                )}
            </Box>
            <Divider />
            <List sx={{ mt: 2 }}>
                {menuItems.map((item) => {
                    if (item.requiredAuth && !user) return null;

                    const isActive = location.pathname === item.path;

                    return (
                        <ListItem
                            key={item.text}
                            disablePadding
                            sx={{ mb: 1 }}
                        >
                            <ListItemButton
                                onClick={() => item.isExternal
                                    ? window.open(item.path, '_blank', 'noopener,noreferrer')
                                    : handleNavigate(item.path)}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '0 24px 24px 0',
                                    pl: 3,
                                    bgcolor: isActive ? `${COLORS.brand.orange.main}15` : 'transparent',
                                    '&:hover': {
                                        bgcolor: isActive ? `${COLORS.brand.orange.main}25` : 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{
                                    minWidth: 40,
                                    color: isActive ? COLORS.brand.orange.main : 'inherit'
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 'bold' : 'regular',
                                        color: isActive ? COLORS.brand.orange.main : 'inherit'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
            <Divider sx={{ mt: 2, mb: 2 }} />
            <Box sx={{ px: 3, display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                <Tooltip title="Google Play" arrow>
                    <IconButton
                        component="a"
                        href="https://play.google.com/store/apps/details?id=com.gobelpower.gobel_vrm"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: effectiveTheme === 'dark' ? '#fff' : 'text.primary',
                            border: '1px solid',
                            borderColor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                            borderRadius: '8px',
                            p: 1.5,
                            '&:hover': {
                                borderColor: COLORS.brand.orange.main,
                                color: COLORS.brand.orange.main,
                                bgcolor: `${COLORS.brand.orange.main}08`
                            }
                        }}
                    >
                        <AndroidIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="App Store" arrow>
                    <IconButton
                        component="a"
                        href="https://apps.apple.com/no/app/gobel-vrm/id6781127381"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: effectiveTheme === 'dark' ? '#fff' : 'text.primary',
                            border: '1px solid',
                            borderColor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                            borderRadius: '8px',
                            p: 1.5,
                            '&:hover': {
                                borderColor: COLORS.brand.orange.main,
                                color: COLORS.brand.orange.main,
                                bgcolor: `${COLORS.brand.orange.main}08`
                            }
                        }}
                    >
                        <AppleIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            </Box>
            <Box sx={{ p: 2, pb: 4, textAlign: 'center' }}>
                <MuiLink
                    component={Link}
                    to="/privacy"
                    variant="caption"
                    color="textSecondary"
                    onClick={() => !isDesktop && onDrawerToggle && onDrawerToggle()}
                    sx={{
                        textDecoration: 'none',
                        opacity: 0.7,
                        '&:hover': {
                            opacity: 1,
                            textDecoration: 'underline',
                            color: COLORS.brand.orange.main
                        }
                    }}
                >
                    Privacy Policy
                </MuiLink>
            </Box>
        </Box>
    );

    return (
        <Box
            component="nav"
            sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
        >
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: drawerWidth,
                        boxShadow: 3,
                        paddingTop: '64px',
                        bgcolor: effectiveTheme === 'dark' ? '#1e1e1e' : COLORS.neutral.background.light
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: drawerWidth,
                        paddingTop: '88px',
                        bgcolor: effectiveTheme === 'dark' ? '#1e1e1e' : COLORS.neutral.background.light,
                        border: 'none'
                    },
                }}
                open
            >
                {drawerContent}
            </Drawer>
        </Box>
    );
};

export { drawerWidth };
export default Sidebar;
